import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { arenaApi } from './api';
import type {
  ArenaTaskDto,
  ArenaRunDto,
  ArenaRunMode,
  ArenaScenarioInfo,
  ArenaAgentAction,
} from './types';
import TaskList, { ScenarioCard } from './TaskList';
import TaskResult from './TaskResult';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { sendCopilotMessage, type CopilotResult } from '../../components/CopilotPanel';

type TabId = 'tasks' | 'scenarios';

const DIFFICULTY_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'basic', label: 'Basic' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

export default function Arena() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // State
  const [activeTab, setActiveTab] = useState<TabId>('tasks');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTask, setSelectedTask] = useState<ArenaTaskDto | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [rightPanel, setRightPanel] = useState<'detail' | 'run' | 'result'>('detail');
  const [resultRunId, setResultRunId] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  // --- Queries ---

  const { data: scenarios, isLoading: scenariosLoading } = useQuery<ArenaScenarioInfo[]>({
    queryKey: ['arena-scenarios'],
    queryFn: arenaApi.listScenarios,
    staleTime: 60_000,
  });

  const { data: tasks, isLoading: tasksLoading } = useQuery<ArenaTaskDto[]>({
    queryKey: ['arena-tasks', statusFilter, difficultyFilter],
    queryFn: () => arenaApi.listTasks({
      status: statusFilter || undefined,
      difficulty: difficultyFilter || undefined,
    }),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.some(t => t.status === 'running');
      return hasRunning ? 3000 : 10000;
    },
  });

  const { data: resultRun, isLoading: resultLoading } = useQuery<ArenaRunDto>({
    queryKey: ['arena-run', resultRunId],
    queryFn: () => arenaApi.getRun(resultRunId!),
    enabled: !!resultRunId,
    staleTime: 5000,
  });

  // --- Mutations ---

  const createTaskMutation = useMutation({
    mutationFn: (scenarioId: string) => arenaApi.createTask(scenarioId),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ['arena-tasks'] });
      addToast(`Task created: ${task.name}`, 'success');
      setActiveTab('tasks');
    },
    onError: (err: Error) => addToast(`Failed to create task: ${err.message}`, 'error'),
  });

  const startRunMutation = useMutation({
    mutationFn: ({ taskId, mode }: { taskId: string; mode: ArenaRunMode }) =>
      arenaApi.startRun(taskId, mode),
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: ['arena-tasks'] });
      setActiveRunId(run.id);
      setRightPanel('run');
    },
    onError: (err: Error) => addToast(`Failed to start run: ${err.message}`, 'error'),
  });

  // NOTE: Do NOT interrupt runs on unmount. The AI copilot navigates to
  // other pages (e.g. persons, invoices) to perform arena tasks, which
  // unmounts this component. The copilot's completion callback already
  // updates the run status. A stale-interrupt here creates false positives.
  // Server-side timeout handles genuinely stuck runs.

  // --- Handlers ---

  const handleSelectTask = useCallback((task: ArenaTaskDto) => {
    setSelectedTask(task);
    setRightPanel('detail');
    setActiveRunId(null);
    setResultRunId(null);

    if (task.lastRunId && ['passed', 'failed', 'error', 'completed'].includes(task.status)) {
      setResultRunId(task.lastRunId);
      setRightPanel('result');
    }
  }, []);

  const handleRun = useCallback((taskId: string, mode: ArenaRunMode) => {
    startRunMutation.mutate(
      { taskId, mode },
      {
        onSuccess: (run) => {
          const task = tasks?.find(t => t.id === taskId);
          const scenario = task ? scenarios?.find(s => s.id === task.scenarioId) : null;
          const description = scenario?.description || task?.name || 'Perform the accounting task';

          const arenaMessage = `[ARENA TASK] Run ID: ${run.id}\n\n${description}\n\nComplete this task using the available tools. After finishing, report what you did.`;

          sendCopilotMessage(arenaMessage, (result: CopilotResult) => {
            const agentActions: ArenaAgentAction[] = result.actions.map((action, idx) => ({
              step: idx + 1,
              tool: action.tool,
              input: typeof action.input === 'string' ? action.input : JSON.stringify(action.input ?? ''),
              result: typeof action.result === 'string'
                ? action.result.substring(0, 500)
                : JSON.stringify(action.result ?? '').substring(0, 500),
              timestamp: new Date().toISOString(),
            }));

            const status = result.error
              ? 'error'
              : agentActions.length === 0
                ? 'failed'
                : 'completed';

            arenaApi.updateRun(run.id, { status, agentActions, errorMessage: result.error })
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['arena-tasks'] });
                queryClient.invalidateQueries({ queryKey: ['arena-run', run.id] });
                setActiveRunId(null);
                setResultRunId(run.id);
                setRightPanel('result');
                if (result.error) {
                  addToast(`Run failed: ${result.error}`, 'error');
                } else if (agentActions.length === 0) {
                  addToast('Run completed with no actions (text-only response)', 'warning');
                } else {
                  addToast(`Run completed: ${agentActions.length} actions performed`, 'success');
                }
              })
              .catch((err: Error) => {
                addToast(`Failed to update run: ${err.message}`, 'error');
              });
          });
        },
      },
    );
  }, [startRunMutation, tasks, scenarios, queryClient, addToast]);

  const handleApprove = useCallback(async (_taskId: string, runId: string) => {
    setApproving(true);
    try {
      await arenaApi.approveRun(runId);
      queryClient.invalidateQueries({ queryKey: ['arena-tasks'] });
      addToast('Run approved', 'success');
    } catch (err) {
      addToast(`Failed to approve: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setApproving(false);
    }
  }, [queryClient, addToast]);

  const handleResultApprove = useCallback(async (runId: string) => {
    setApproving(true);
    try {
      await arenaApi.approveRun(runId);
      queryClient.invalidateQueries({ queryKey: ['arena-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['arena-run', runId] });
      addToast('Run approved', 'success');
    } catch (err) {
      addToast(`Failed to approve: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setApproving(false);
    }
  }, [queryClient, addToast]);

  const handleRetry = useCallback((taskId: string) => {
    handleRun(taskId, 'api');
  }, [handleRun]);

  const handleValidate = useCallback(async (runId: string) => {
    try {
      await arenaApi.validateRun(runId);
      queryClient.invalidateQueries({ queryKey: ['arena-run', runId] });
      addToast('Re-validation complete', 'info');
    } catch (err) {
      addToast(`Validation failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    }
  }, [queryClient, addToast]);

  // --- Filtered tasks ---

  const filteredTasks = useMemo(() => {
    let result = tasks ?? [];
    if (difficultyFilter) result = result.filter(t => t.difficulty === difficultyFilter);
    if (statusFilter) result = result.filter(t => t.status === statusFilter);
    return result;
  }, [tasks, difficultyFilter, statusFilter]);

  // --- Summary stats ---

  const stats = useMemo(() => {
    const all = tasks ?? [];
    return {
      total: all.length,
      passed: all.filter(t => t.status === 'passed' || t.status === 'completed').length,
      failed: all.filter(t => t.status === 'failed' || t.status === 'error').length,
      running: all.filter(t => t.status === 'running').length,
      ready: all.filter(t => t.status === 'ready').length,
    };
  }, [tasks]);

  // --- Render ---

  const isLoading = (activeTab === 'tasks' && tasksLoading) || (activeTab === 'scenarios' && scenariosLoading);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Arena</h1>
          <p className="text-sm text-gray-500 mt-1">
            Run AI agents against accounting tasks and validate results
          </p>
        </div>
        <button
          onClick={() => setActiveTab(activeTab === 'tasks' ? 'scenarios' : 'tasks')}
          className="px-4 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 border border-primary-200 transition-colors"
        >
          {activeTab === 'tasks' ? 'Browse Scenarios' : 'View Tasks'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        <StatCard label="Total" value={stats.total} color="gray" />
        <StatCard label="Ready" value={stats.ready} color="amber" />
        <StatCard label="Running" value={stats.running} color="blue" />
        <StatCard label="Passed" value={stats.passed} color="green" />
        <StatCard label="Failed" value={stats.failed} color="red" />
      </div>

      {/* Filters (tasks tab only) */}
      {activeTab === 'tasks' && (
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-xs text-gray-500 font-medium">Difficulty:</span>
          {DIFFICULTY_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setDifficultyFilter(f.value)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                difficultyFilter === f.value
                  ? 'bg-primary-100 text-primary-700 border-primary-200'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="mx-2 text-gray-300">|</span>
          <span className="text-xs text-gray-500 font-medium">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All</option>
            <option value="ready">Ready</option>
            <option value="running">Running</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="error">Error</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left panel: task/scenario list */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                {activeTab === 'tasks' ? `Tasks (${filteredTasks.length})` : `Scenarios (${scenarios?.length ?? 0})`}
              </h2>
              <div className="max-h-[calc(100vh-380px)] overflow-y-auto">
                {activeTab === 'tasks' ? (
                  <TaskList
                    tasks={filteredTasks}
                    selectedTaskId={selectedTask?.id ?? null}
                    onSelect={handleSelectTask}
                    onRun={handleRun}
                    onApprove={handleApprove}
                    loading={false}
                  />
                ) : (
                  <div className="space-y-2">
                    {(scenarios ?? []).map(scenario => (
                      <ScenarioCard
                        key={scenario.id}
                        scenario={scenario}
                        onCreate={(id) => createTaskMutation.mutate(id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="lg:col-span-3">
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 min-h-[300px]">
              {!selectedTask && !activeRunId && !resultRunId && (
                <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                  Select a task from the list to view details.
                </div>
              )}

              {selectedTask && rightPanel === 'detail' && !activeRunId && !resultRunId && (
                <TaskDetail task={selectedTask} />
              )}

              {activeRunId && rightPanel === 'run' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-700">Run in Progress</h3>
                    <span className="text-xs text-gray-400 font-mono">{activeRunId}</span>
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500" />
                    </span>
                  </div>
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-sm text-primary-700">
                    The AI Copilot is working on this task. Watch the copilot panel on the right for live actions.
                  </div>
                </div>
              )}

              {resultRunId && rightPanel === 'result' && resultRun && !resultLoading && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-700">Run Results</h3>
                    <span className="text-xs text-gray-400 font-mono">{resultRunId}</span>
                  </div>
                  <TaskResult
                    run={resultRun}
                    onApprove={handleResultApprove}
                    onRetry={handleRetry}
                    onValidate={handleValidate}
                    approving={approving}
                  />
                </div>
              )}

              {resultRunId && resultLoading && <LoadingSpinner />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: 'gray' | 'amber' | 'blue' | 'green' | 'red' }) {
  const bgColors = {
    gray: 'bg-gray-50 border-gray-200',
    amber: 'bg-amber-50 border-amber-200',
    blue: 'bg-primary-50 border-primary-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
  };
  const textColors = {
    gray: 'text-gray-700',
    amber: 'text-amber-700',
    blue: 'text-primary-700',
    green: 'text-green-700',
    red: 'text-red-700',
  };

  return (
    <div className={`rounded-lg border p-3 text-center ${bgColors[color]}`}>
      <div className={`text-2xl font-bold ${textColors[color]}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function TaskDetail({ task }: { task: ArenaTaskDto }) {
  const isRunning = task.status === 'running';
  const isPassed = task.status === 'passed' || task.status === 'completed';
  const isFailed = task.status === 'failed' || task.status === 'error';

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{task.name}</h3>
        <div className="flex items-center gap-2 mt-2">
          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold uppercase ${
            task.difficulty === 'basic' ? 'bg-green-100 text-green-700' :
            task.difficulty === 'intermediate' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            {task.difficulty}
          </span>
          <span className="text-xs text-gray-500">{task.category}</span>
          <span className={`text-xs font-medium ${
            isRunning ? 'text-primary-600' : isPassed ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-gray-500'
          }`}>
            {task.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-lg font-semibold text-gray-700">{task.totalRuns}</div>
          <div className="text-xs text-gray-500">Total Runs</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-lg font-semibold text-green-600">{task.passedRuns}</div>
          <div className="text-xs text-gray-500">Passed</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-lg font-semibold text-red-600">{task.failedRuns}</div>
          <div className="text-xs text-gray-500">Failed</div>
        </div>
      </div>

      <div className="text-xs text-gray-500 space-y-1">
        <div>Task ID: <code className="font-mono">{task.id}</code></div>
        <div>Scenario ID: <code className="font-mono">{task.scenarioId}</code></div>
        <div>Created: {new Date(task.createdAt).toLocaleString()}</div>
        {task.lastRunAt && <div>Last run: {new Date(task.lastRunAt).toLocaleString()}</div>}
      </div>
    </div>
  );
}
